import { useEffect, useState, useCallback, useMemo } from 'react';
import TableComponent from '../../components/Table';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { fetchGroup } from '../../features/groups/groupsSlice';
import { ACTIONS } from '../../constants/actionTables';
import { getIntervals, getIntervalsNotify } from '../../constants/options';
import ModalAddMaintanenceTask from '../../components/ModalAddMaintanenceTask';
import ModalStatusGroup from '../../components/ModalStatusGroup';
import ModalAddManual from '../../components/ModalAddManual';
import ModalAddSupplyGroup from '../../components/ModalAddSupplyGroup';
import { requestSearch } from '../../helpers/search';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import styles from './style.module.css';
import { useAuth } from '../../hooks/useAuth';
import {
  useApproveMutation,
  useRequestDeleteMutation,
  useConfirmDeleteMutation,
} from '../../app/services/maintenances';

function SpecificGroup() {
  const [dataMaintanence, setDataMaintanence] = useState(null);
  const [dataManuals, setDataManuals] = useState(null);
  const [suppliesData, setSuppliesData] = useState(null);
  const [suppliesList, setSuppliesList] = useState([]);
  const [group, setGroup] = useState(null);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [pendingTaskIds, setPendingTaskIds] = useState(() => new Set());
  const [approveConfirmId, setApproveConfirmId] = useState(null);
  const [approveBusy, setApproveBusy] = useState(false);
  const [deleteRequestId, setDeleteRequestId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Filtered data for search
  const [filteredTasksData, setFilteredTasksData] = useState([]);
  const [filteredManualsData, setFilteredManualsData] = useState([]);
  const [filteredSuppliesData, setFilteredSuppliesData] = useState([]);

  // Sort configurations for three-click sorting
  const [tasksSortConfig, setTasksSortConfig] = useState({ key: null, direction: 'asc' });
  const [manualsSortConfig, setManualsSortConfig] = useState({ key: null, direction: 'asc' });
  const [suppliesSortConfig, setSuppliesSortConfig] = useState({ key: null, direction: 'asc' });

  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const intervals = useMemo(() => getIntervals(t), [t]);
  const intervalsNotify = useMemo(() => getIntervalsNotify(t), [t]);

  const dispatch = useDispatch();
  const [approveMaintenance] = useApproveMutation();
  const [requestDeleteMaintenance] = useRequestDeleteMutation();
  const [confirmDeleteMaintenance] = useConfirmDeleteMutation();

  const fetchGroupId = useCallback(async () => {
    try {
      const response = await dispatch(fetchGroup(id));
      setGroup(response.payload);
    } catch (error) {
      console.error('Error fetching maintenance data:', error);
    }
  }, [dispatch, id]);

  const handleApproveConfirmed = useCallback(async () => {
    if (!approveConfirmId) return;
    setApproveBusy(true);
    try {
      await approveMaintenance(approveConfirmId).unwrap();
      await fetchGroupId();
      setApproveConfirmId(null);
    } catch (err) {
      const msg = err?.data?.message || 'Approve failed';
      // eslint-disable-next-line no-alert
      window.alert(msg);
    } finally {
      setApproveBusy(false);
    }
  }, [approveConfirmId, approveMaintenance, fetchGroupId]);

  const handleApproveRow = useCallback((maintenanceId) => {
    setApproveConfirmId(maintenanceId);
  }, []);

  const handleRequestDeleteRow = useCallback((maintenanceId) => {
    setDeleteRequestId(maintenanceId);
  }, []);

  const handleRequestDeleteConfirmed = useCallback(async () => {
    if (!deleteRequestId) return;
    setDeleteBusy(true);
    try {
      await requestDeleteMaintenance(deleteRequestId).unwrap();
      await fetchGroupId();
      setDeleteRequestId(null);
    } catch (err) {
      const msg = err?.data?.message || 'Delete request failed';
      // eslint-disable-next-line no-alert
      window.alert(msg);
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteRequestId, requestDeleteMaintenance, fetchGroupId]);

  const handleConfirmDeleteRow = useCallback((maintenanceId) => {
    setConfirmDeleteId(maintenanceId);
  }, []);

  const handleConfirmDeleteConfirmed = useCallback(async () => {
    if (!confirmDeleteId) return;
    setDeleteBusy(true);
    try {
      await confirmDeleteMaintenance(confirmDeleteId).unwrap();
      await fetchGroupId();
      setConfirmDeleteId(null);
    } catch (err) {
      const msg = err?.data?.message || 'Delete confirmation failed';
      // eslint-disable-next-line no-alert
      window.alert(msg);
    } finally {
      setDeleteBusy(false);
    }
  }, [confirmDeleteId, confirmDeleteMaintenance, fetchGroupId]);

  const isAdmin = user && user.role === 'admin';
  const titlesTask = [
    t('generic.number'),
    t('generic.name'),
    t('generic.interval'),
    t('specificGroup.NotificationLeadTime'),
    t('generic.maintenanceType'),
  ];
  if (isAdmin) {
    titlesTask.push(t('approval.pendingApproval'));
    titlesTask.push(t('generic.action'));
  }

  const titlesSupplies = [
    t('generic.number'),
    t('generic.name'),
    t('generic.description'),
    t('generic.manufacture'),
    t('generic.safetySheet'),
    t('generic.action'),
  ];
  const titlesManuals = [
    t('generic.number'),
    t('generic.name'),
    t('generic.description'),
    t('generic.safetySheet'),
    t('generic.action'),
  ];

  useEffect(() => {
    fetchGroupId();
  }, [id, fetchGroupId]);

  useEffect(() => {
    if (group) {
      const visibleMaintenances = isAdmin
        ? group.maintenances
        : group.maintenances.filter(
            (m) => (m.approval_status || 'active') !== 'pending_approval'
          );
      const maintenanceData = visibleMaintenances.map((maintenance) => {
        let parsedInterval = '';
        try {
          parsedInterval = JSON.parse(maintenance.interval);
        } catch (error) {
          console.error('Error parsing interval:', error);
          parsedInterval = { type: 'day', value: 1 }; // fallback
        }

        const status = maintenance.approval_status || 'active';
        const canApprove =
          status === 'pending_approval' &&
          user?.role === 'admin' &&
          +user.id !== +maintenance.created_by;
        const canConfirmDelete =
          status === 'pending_deletion' &&
          user?.role === 'admin' &&
          +user.id !== +maintenance.created_by;
        const canRequestDelete =
          status !== 'pending_deletion' && user?.role === 'admin';
        const statusChipLabel =
          status === 'pending_approval'
            ? t('approval.pendingApproval')
            : status === 'pending_deletion'
            ? t('approval.pendingDeletion')
            : t(`approval.${status}`);
        const statusChipColor =
          status === 'pending_approval'
            ? 'warning'
            : status === 'pending_deletion'
            ? 'error'
            : status === 'active'
            ? 'success'
            : 'default';
        return {
          id: maintenance.id,
          number: maintenance.id,
          name: maintenance.name,
          interval: findIntervalDescription(parsedInterval, intervals),
          notificationLeadTime: findIntervalNotifyDescription(
            maintenance.notification_lead_time,
            intervalsNotify
          ),
          maintenanceType: maintenance.maintenance_type?.name || 'N/A',
          ...(isAdmin && {
          approvalStatus: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label={statusChipLabel} color={statusChipColor} />
              {canApprove && (
                <Button
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApproveRow(maintenance.id);
                  }}
                  sx={{
                    textTransform: 'none',
                    height: 36,
                    minWidth: 96,
                    fontWeight: 600,
                    backgroundColor: '#1565c0',
                    color: '#fff',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#0d47a1', boxShadow: 'none' },
                  }}
                >
                  {t('approval.approve')}
                </Button>
              )}
              {canConfirmDelete && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConfirmDeleteRow(maintenance.id);
                  }}
                  sx={{
                    textTransform: 'none',
                    height: 36,
                    minWidth: 96,
                    fontWeight: 600,
                    boxShadow: 'none',
                  }}
                >
                  {t('approval.confirmDeletion')}
                </Button>
              )}
              {canRequestDelete && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRequestDeleteRow(maintenance.id);
                  }}
                  sx={{ textTransform: 'none', height: 36, minWidth: 80 }}
                >
                  {t('approval.requestDeletion')}
                </Button>
              )}
            </Box>
          ),
          }),
        };
      });
      setDataMaintanence(maintenanceData);
      setFilteredTasksData(maintenanceData);
      setPendingTaskIds(
        new Set(
          group.maintenances
            .filter((m) => (m.approval_status || 'active') === 'pending_approval')
            .map((m) => m.id)
        )
      );

      const data = group.manuals.map((manual) => ({
        id: manual.id,
        number: manual.id,
        name: manual.name,
        description: manual.description,
        safetySheet: manual.original_filename,
        link: manual.filename,
      }));
      setDataManuals(data);
      setFilteredManualsData(data);

      const suppliesData = group.machine_supplies.map((supply) => ({
        id: supply.id,
        number: supply.supply.id,
        name: supply.supply.name,
        description: supply.supply.description,
        manufacture: supply.supply.manufacture,
        link: supply.supply.filename,
        safetySheet: supply.supply.original_filename,
      }));
      setSuppliesData(suppliesData);
      setFilteredSuppliesData(suppliesData);
      const suppliesIds = group.machine_supplies.map((supply) => supply.supply.id);

      setSuppliesList(suppliesIds);
    }
  }, [
    group,
    intervals,
    intervalsNotify,
    user,
    handleApproveRow,
    handleRequestDeleteRow,
    handleConfirmDeleteRow,
    t,
  ]);

  const findIntervalDescription = (value, intervalData) => {
    const interval = intervalData.find(
      (item) => item.value.type === value.type && item.value.value === value.value
    );
    return interval ? interval.period : '';
  };

  const findIntervalNotifyDescription = (value, intervalData) => {
    const interval = intervalData.find((item) => item.value === value);
    return interval ? interval.period : '';
  };

  // Search handlers
  const handleTasksSearch = (searchValue) => {
    requestSearch(searchValue, dataMaintanence || [], setFilteredTasksData);
  };

  const handleManualsSearch = (searchValue) => {
    requestSearch(searchValue, dataManuals || [], setFilteredManualsData);
  };

  const handleSuppliesSearch = (searchValue) => {
    requestSearch(searchValue, suppliesData || [], setFilteredSuppliesData);
  };

  // Sort handlers with three-click functionality
  const handleTasksSort = (columnKey) => {
    let direction = 'asc';
    let newSortConfig = { key: columnKey, direction };

    if (tasksSortConfig.key === columnKey) {
      if (tasksSortConfig.direction === 'asc') {
        direction = 'desc';
        newSortConfig = { key: columnKey, direction };
      } else if (tasksSortConfig.direction === 'desc') {
        // Reset sorting - show original data
        newSortConfig = { key: null, direction: 'asc' };
        setTasksSortConfig(newSortConfig);
        setFilteredTasksData(dataMaintanence);
        return;
      }
    }

    setTasksSortConfig(newSortConfig);

    const sortedData = [...filteredTasksData].sort((a, b) => {
      if (a[columnKey] < b[columnKey]) {
        return direction === 'asc' ? -1 : 1;
      }
      if (a[columnKey] > b[columnKey]) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredTasksData(sortedData);
  };

  const handleManualsSort = (columnKey) => {
    let direction = 'asc';
    let newSortConfig = { key: columnKey, direction };

    if (manualsSortConfig.key === columnKey) {
      if (manualsSortConfig.direction === 'asc') {
        direction = 'desc';
        newSortConfig = { key: columnKey, direction };
      } else if (manualsSortConfig.direction === 'desc') {
        // Reset sorting - show original data
        newSortConfig = { key: null, direction: 'asc' };
        setManualsSortConfig(newSortConfig);
        setFilteredManualsData(dataManuals);
        return;
      }
    }

    setManualsSortConfig(newSortConfig);

    const sortedData = [...filteredManualsData].sort((a, b) => {
      if (a[columnKey] < b[columnKey]) {
        return direction === 'asc' ? -1 : 1;
      }
      if (a[columnKey] > b[columnKey]) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredManualsData(sortedData);
  };

  const handleSuppliesSort = (columnKey) => {
    let direction = 'asc';
    let newSortConfig = { key: columnKey, direction };

    if (suppliesSortConfig.key === columnKey) {
      if (suppliesSortConfig.direction === 'asc') {
        direction = 'desc';
        newSortConfig = { key: columnKey, direction };
      } else if (suppliesSortConfig.direction === 'desc') {
        // Reset sorting - show original data
        newSortConfig = { key: null, direction: 'asc' };
        setSuppliesSortConfig(newSortConfig);
        setFilteredSuppliesData(suppliesData);
        return;
      }
    }

    setSuppliesSortConfig(newSortConfig);

    const sortedData = [...filteredSuppliesData].sort((a, b) => {
      if (a[columnKey] < b[columnKey]) {
        return direction === 'asc' ? -1 : 1;
      }
      if (a[columnKey] > b[columnKey]) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredSuppliesData(sortedData);
  };

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          <Link
            to={`/machines-groups`}
            style={{ textDecoration: 'none', color: 'var(--text-heading)' }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ opacity: 0.5 }}>
              {t('specificGroup.machineGroups')}
            </Typography>
          </Link>
          <Typography variant="h5" fontWeight="bold">
            {group && group.name} {t('generic.group')}
          </Typography>
        </div>
        {user && user.role === 'admin' && (
          <ModalStatusGroup id={id} action={'archived'} size="large" />
        )}
      </div>
      <Divider variant="fullWidth " flexItem />

      <div className={styles.header} style={{ margin: '20px 0' }}>
        <Typography variant="h6" fontWeight="bold">
          {t('specificGroup.maintenanceTasks')}
        </Typography>
        {user && user.role === 'admin' && (
          <ModalAddMaintanenceTask type={'add'} fetchData={fetchGroupId} groupId={group?.id} />
        )}
      </div>

      {isAdmin && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, alignSelf: 'flex-start' }}>
          <Chip
            label={t('generic.all')}
            color={showPendingOnly ? 'default' : 'primary'}
            onClick={() => setShowPendingOnly(false)}
            variant={showPendingOnly ? 'outlined' : 'filled'}
          />
          <Chip
            label={t('approval.pendingApproval')}
            color={showPendingOnly ? 'warning' : 'default'}
            onClick={() => setShowPendingOnly(true)}
            variant={showPendingOnly ? 'filled' : 'outlined'}
          />
        </Stack>
      )}

      <TableComponent
        titles={titlesTask}
        data={
          showPendingOnly
            ? filteredTasksData.filter((r) => pendingTaskIds.has(r.id))
            : filteredTasksData
        }
        fetchData={fetchGroupId}
        action={isAdmin ? ACTIONS.SPECIFIC_GROUP_MAINTENANCE_TASK : null}
        search={true}
        showTableHeader={true}
        tableTitle={t('generic.allTasks')}
        tableCount={`${dataMaintanence?.length || 0} ${t('generic.tasksText')}`}
        searchPlaceholder={t('generic.search')}
        onSearch={handleTasksSearch}
        onSort={handleTasksSort}
        sortConfig={tasksSortConfig}
        sortableColumns={[
          'number',
          'name',
          'interval',
          'notificationLeadTime',
          'maintenanceType',
        ]}
        columnKeys={[
          'number',
          'name',
          'interval',
          'notificationLeadTime',
          'maintenanceType',
          'approvalStatus',
          'action',
        ]}
      />

      <div className={styles.header} style={{ marginTop: '40px' }}>
        <Typography variant="h6" fontWeight="bold">
          {t('generic.manuals')}
        </Typography>

        {user && user.role === 'admin' && <ModalAddManual id={id} fetchData={fetchGroupId} />}
      </div>

      <TableComponent
        titles={titlesManuals}
        data={filteredManualsData}
        fetchData={fetchGroupId}
        action={ACTIONS.SPECIFIC_GROUP_MANUALS}
        search={true}
        showTableHeader={true}
        tableTitle={t('generic.allManuals')}
        tableCount={`${dataManuals?.length || 0} ${t('generic.manualsText')}`}
        searchPlaceholder={t('generic.search')}
        onSearch={handleManualsSearch}
        onSort={handleManualsSort}
        sortConfig={manualsSortConfig}
        sortableColumns={['number', 'name', 'description', 'safetySheet']}
        columnKeys={['number', 'name', 'description', 'safetySheet', 'action']}
      />

      <div className={styles.header} style={{ marginTop: '40px' }}>
        <Typography variant="h6" fontWeight="bold">
          {t('generic.supplies')}
        </Typography>

        {user && user.role === 'admin' && (
          <ModalAddSupplyGroup
            id={id}
            fetchData={fetchGroupId}
            selectedSuppliesList={suppliesList}
          />
        )}
      </div>

      <TableComponent
        titles={titlesSupplies}
        data={filteredSuppliesData}
        fetchData={fetchGroupId}
        action={ACTIONS.SPECIFIC_GROUP_SUPPLIES}
        search={true}
        showTableHeader={true}
        tableTitle={t('generic.allSupplies')}
        tableCount={`${suppliesData?.length || 0} ${t('generic.suppliesText')}`}
        searchPlaceholder={t('generic.search')}
        onSearch={handleSuppliesSearch}
        onSort={handleSuppliesSort}
        sortConfig={suppliesSortConfig}
        sortableColumns={['number', 'name', 'description', 'manufacture', 'safetySheet']}
        columnKeys={['number', 'name', 'description', 'manufacture', 'safetySheet', 'action']}
      />

      <Dialog
        open={approveConfirmId !== null}
        onClose={() => !approveBusy && setApproveConfirmId(null)}
      >
        <DialogTitle>{t('approval.confirmApproveTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {t('approval.confirmApproveMessage')}
            {(() => {
              const target = group?.maintenances?.find(
                (m) => +m.id === +approveConfirmId
              );
              return target ? (
                <div style={{ marginTop: 8, fontWeight: 600, color: 'rgba(0,0,0,0.87)' }}>
                  «{target.name}»
                </div>
              ) : null;
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setApproveConfirmId(null)}
            disabled={approveBusy}
          >
            {t('modals.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleApproveConfirmed}
            disabled={approveBusy}
            sx={{
              backgroundColor: '#ed6c02',
              color: '#fff',
              '&:hover': { backgroundColor: '#c75800' },
            }}
          >
            {t('approval.approve')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteRequestId !== null}
        onClose={() => !deleteBusy && setDeleteRequestId(null)}
      >
        <DialogTitle>{t('approval.requestDeletionTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {t('approval.requestDeletionMessage')}
            {(() => {
              const target = group?.maintenances?.find(
                (m) => +m.id === +deleteRequestId
              );
              return target ? (
                <div style={{ marginTop: 8, fontWeight: 600, color: 'rgba(0,0,0,0.87)' }}>
                  «{target.name}»
                </div>
              ) : null;
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setDeleteRequestId(null)}
            disabled={deleteBusy}
          >
            {t('modals.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRequestDeleteConfirmed}
            disabled={deleteBusy}
          >
            {t('approval.requestDeletion')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeleteId !== null}
        onClose={() => !deleteBusy && setConfirmDeleteId(null)}
      >
        <DialogTitle>{t('approval.confirmDeletionTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {t('approval.confirmDeletionMessage')}
            {(() => {
              const target = group?.maintenances?.find(
                (m) => +m.id === +confirmDeleteId
              );
              return target ? (
                <div style={{ marginTop: 8, fontWeight: 600, color: 'rgba(0,0,0,0.87)' }}>
                  «{target.name}»
                </div>
              ) : null;
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setConfirmDeleteId(null)}
            disabled={deleteBusy}
          >
            {t('modals.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteConfirmed}
            disabled={deleteBusy}
          >
            {t('approval.confirmDeletion')}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}

export default SpecificGroup;
